import { Link, Outlet, useLocation } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  BanknoteIcon,
  CalendarRangeIcon,
  ChevronDownIcon,
  FileSearchIcon,
  LayersIcon,
  type LucideIcon,
  ScaleIcon,
  StoreIcon,
  TrendingUpIcon,
} from 'lucide-react'

import {
  DropdownMenuItem,
  InsightsDuplicatesSkeleton,
  InsightsFlowOfFundsSkeleton,
  InsightsIncomeSkeleton,
  InsightsSpendingSkeleton,
  PriorityNav,
  RouteSuspense,
  SectionNavItem,
} from '@/components/ui'
import { useReconciliationEntitled } from '@/modules/account'
import {
  duplicatePageGroups,
  OverviewNotices,
  useFetchInsights,
  useInsights,
  useInsightsMutating,
  useLoadingInsights,
  useLoadingProject,
  useProject,
  useStatements,
} from '@/modules/projects'
import {
  hasInsightsResult,
  isInsightsProcessing,
  isProjectProcessing,
} from '@/modules/projects/status'
import { cn, getProjectPath } from '@/utils'
import {
  InsightsGenerateSection,
  RegenerateInsightsDialog,
} from '../components'

interface InsightsLayoutPageProps {
  slug: string
}

interface InsightsTab {
  label: string
  path: string
  icon: LucideIcon
}

// Always-on views derive from data already in the store, so they need no generation pass.
const ALWAYS_ON_TABS: InsightsTab[] = [
  { label: 'Coverage', path: '/coverage', icon: CalendarRangeIcon },
  { label: 'Categories', path: '/categories', icon: LayersIcon },
  { label: 'Reconciliation', path: '/reconciliation', icon: ScaleIcon },
]

// Generated views need the backend insights pass; before it runs they show the Generate action.
const AI_TABS: InsightsTab[] = [
  { label: 'Income', path: '/income', icon: TrendingUpIcon },
  { label: 'Merchants', path: '/merchants', icon: StoreIcon },
  { label: 'Anomalies', path: '/anomalies', icon: AlertTriangleIcon },
  { label: 'Checks', path: '/checks', icon: BanknoteIcon },
]

const INSIGHTS_TABS = [...ALWAYS_ON_TABS, ...AI_TABS]
const ALWAYS_ON_PATHS = ALWAYS_ON_TABS.map((tab) => tab.path)

// The reconcile verdict is premium, but reading a cycle beside its statement pages is not. An
// account without the feature gets the same tab under a name that promises nothing about balances.
const CYCLES_TAB: InsightsTab = {
  label: 'Statement cycles',
  path: '/reconciliation',
  icon: FileSearchIcon,
}

const tabsFor = (isReconciliationEntitled: boolean) =>
  isReconciliationEntitled
    ? INSIGHTS_TABS
    : INSIGHTS_TABS.map((tab) =>
        tab.path === CYCLES_TAB.path ? CYCLES_TAB : tab,
      )

function getSkeletonForPath(pathname: string) {
  if (pathname.includes('/categories')) return <InsightsSpendingSkeleton />
  if (pathname.includes('/merchants')) return <InsightsSpendingSkeleton />
  if (pathname.includes('/duplicates')) return <InsightsDuplicatesSkeleton />
  if (pathname.includes('/flow-of-funds'))
    return <InsightsFlowOfFundsSkeleton />
  if (pathname.includes('/network')) return <InsightsFlowOfFundsSkeleton />
  if (pathname.includes('/anomalies')) return <InsightsDuplicatesSkeleton />
  if (pathname.includes('/checks')) return <InsightsDuplicatesSkeleton />
  return <InsightsIncomeSkeleton />
}

export function InsightsLayoutPage({ slug }: InsightsLayoutPageProps) {
  const insights = useInsights()
  const project = useProject()
  const statements = useStatements()
  const isFetchingProject = useLoadingProject()
  const isFetchingInsights = useLoadingInsights()
  const isMutating = useInsightsMutating()
  const isReconciliationEntitled = useReconciliationEntitled()
  const location = useLocation()

  useFetchInsights(slug)

  if (isFetchingInsights || isFetchingProject || !insights || !project)
    return <InsightsIncomeSkeleton />

  const basePath = `${getProjectPath({ slug })}/insights`

  const visibleTabs = tabsFor(isReconciliationEntitled)

  // Always-on views read live store data, so they stay open while the project processes. AI views
  // wait: lukita keeps the prior run's insights status, so without this gate they would show
  // last run's numbers under a "still processing" pill.
  const isAlwaysOnView = ALWAYS_ON_PATHS.some((path) =>
    location.pathname.includes(path),
  )
  // Repeated page ranges come from segment tags already in the store, not the insights pass. Unlock
  // Duplicates when there are repeats even before insights run; it offers to generate the rest.
  const showRepeatedPages =
    location.pathname.includes('/duplicates') &&
    duplicatePageGroups(statements).length > 0
  const isProcessing = isProjectProcessing(project)
  const showContent =
    isAlwaysOnView ||
    showRepeatedPages ||
    (!isProcessing && hasInsightsResult(insights))

  const isTabActive = (tab: InsightsTab) => location.pathname.includes(tab.path)

  const renderTab = (tab: InsightsTab) => {
    const Icon = tab.icon
    return (
      <SectionNavItem asChild active={isTabActive(tab)}>
        <Link to={`${basePath}${tab.path}`} className="gap-1.5">
          <Icon className="size-4 text-emerald-500" />
          {tab.label}
        </Link>
      </SectionNavItem>
    )
  }

  const renderMenuTab = (tab: InsightsTab) => {
    const Icon = tab.icon
    return (
      <DropdownMenuItem asChild>
        <Link
          to={`${basePath}${tab.path}`}
          className={cn(
            'cursor-pointer gap-2',
            isTabActive(tab) && 'font-medium text-foreground',
          )}
        >
          <Icon className="size-4 text-emerald-500" />
          {tab.label}
        </Link>
      </DropdownMenuItem>
    )
  }

  const renderMoreTrigger = ({ active }: { active: boolean }) => (
    <button
      type="button"
      className={cn(
        'flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap border-transparent border-b-2 py-2.5 font-medium text-sm outline-none transition-colors',
        active
          ? 'border-foreground text-foreground'
          : 'text-muted-foreground hover:text-foreground',
        'data-[state=open]:text-foreground',
      )}
    >
      More
      <ChevronDownIcon className="size-4" />
    </button>
  )

  return (
    <>
      <RegenerateInsightsDialog />

      <div className="space-y-4">
        <OverviewNotices slug={slug} />
        <PriorityNav
          items={visibleTabs}
          getKey={(tab) => tab.path}
          isItemActive={isTabActive}
          renderItem={renderTab}
          renderMenuItem={renderMenuTab}
          renderMoreTrigger={renderMoreTrigger}
          className="mb-6 border-border border-b px-1"
          gap={24}
        />

        {showContent ? (
          <RouteSuspense fallback={getSkeletonForPath(location.pathname)}>
            <Outlet />
          </RouteSuspense>
        ) : (
          <InsightsGenerateSection
            project={project}
            isGenerating={
              isMutating || isInsightsProcessing(insights) || isProcessing
            }
          />
        )}
      </div>
    </>
  )
}
